import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { DollarSign, TrendingUp, ShoppingBag, Target, ArrowUpRight, ArrowDownRight, Layers } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RevenueDashboard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/admin/revenue-dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (loading || !data) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-neutral-400">
          <div className="animate-pulse">Loading revenue data...</div>
        </CardContent>
      </Card>
    );
  }

  const orderDelta = data.orders_last_month > 0
    ? Math.round((data.orders_this_month - data.orders_last_month) / data.orders_last_month * 100)
    : data.orders_this_month > 0 ? 100 : 0;

  return (
    <div className="space-y-4" data-testid="revenue-dashboard">
      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-3">
        <MetricCard icon={DollarSign} label="Total Revenue" value={`$${(data.total_revenue || 0).toLocaleString()}`} color="green" />
        <MetricCard icon={ShoppingBag} label="Orders This Month" value={data.orders_this_month} delta={orderDelta} color="blue" />
        <MetricCard icon={Target} label="Avg Order Value" value={`$${(data.avg_order_value || 0).toLocaleString()}`} color="amber" />
        <MetricCard icon={TrendingUp} label="Conversion Rate" value={`${data.conversion_rate}%`} subtitle={`${data.total_orders} of ${data.total_leads} leads`} color="purple" />
      </div>

      {/* Revenue Chart + Top Fabrics */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-600" /> Monthly Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48" data-testid="revenue-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.monthly_revenue} barSize={32}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="month_short" tick={{ fontSize: 12, fill: '#999' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#999' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #e5e5e5', fontSize: 13 }}
                    formatter={(v) => [`$${Number(v).toLocaleString()}`, 'Revenue']}
                    labelFormatter={(l) => data.monthly_revenue.find(m => m.month_short === l)?.month || l}
                  />
                  <Bar dataKey="revenue" fill="#d97706" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Layers className="w-4 h-4 text-amber-600" /> Top Fabrics</CardTitle>
            <CardDescription className="text-xs">By orders placed</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="top-fabrics">
              {data.top_fabrics.length === 0 && <p className="text-xs text-neutral-400">No data yet</p>}
              {data.top_fabrics.map((f, i) => {
                const maxCount = data.top_fabrics[0]?.count || 1;
                return (
                  <div key={i} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-neutral-700 truncate max-w-[140px]">{f.name}</span>
                      <span className="text-neutral-400 tabular-nums">{f.count}</span>
                    </div>
                    <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full" style={{ width: `${(f.count / maxCount) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, delta, subtitle, color }) {
  const colors = {
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors[color]}`}>
            <Icon className="w-4 h-4" />
          </div>
          <span className="text-xs text-neutral-500 uppercase tracking-wide">{label}</span>
        </div>
        <p className="text-2xl font-bold text-neutral-800">{value}</p>
        {delta !== undefined && (
          <p className={`text-xs mt-1 flex items-center gap-0.5 ${delta >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            {delta >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(delta)}% vs last month
          </p>
        )}
        {subtitle && <p className="text-xs text-neutral-400 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}
